import { cn } from '@today/ui-primitives'
import { cloneElement, type ReactElement, useId } from 'react'

/**
 * Props for the {@link FieldShell} molecule.
 *
 * @property label - Visible label; becomes the control's accessible name.
 * @property description - Optional helper text referenced by `aria-describedby`.
 * @property error - Optional error text; its presence marks the control invalid.
 * @property children - Exactly one control element to label and describe.
 * @property className - Extra Tailwind classes merged onto the container.
 *
 * @example
 * ```tsx
 * <FieldShell label="Email" description="We never share it" error={errors.email}>
 *   <input type="email" />
 * </FieldShell>
 * ```
 */
export interface FieldShellProps {
	label: string
	description?: string
	error?: string
	children: ReactElement
	className?: string
}

/** The a11y wiring a labelled control needs, given which optional texts are present. */
interface ControlAria {
	id: string
	'aria-labelledby': string
	'aria-describedby'?: string
	'aria-invalid'?: 'true'
}

/**
 * Builds the control's id and description/validity wiring.
 *
 * Shared with {@link SelectField} so both halves of the field contract stay
 * identical; exported for that reason, not as a public API.
 */
export function fieldAria(baseId: string, hasDescription: boolean, hasError: boolean): ControlAria {
	const describedBy = [hasDescription ? `${baseId}-description` : null, hasError ? `${baseId}-error` : null]
		.filter(Boolean)
		.join(' ')
	return {
		id: `${baseId}-control`,
		// `aria-labelledby` as well as `htmlFor`, because `<label for>` only binds
		// to labellable elements — a `<button>`-triggered control (SelectField)
		// would otherwise have no accessible name.
		'aria-labelledby': `${baseId}-label`,
		...(describedBy ? { 'aria-describedby': describedBy } : {}),
		...(hasError ? { 'aria-invalid': 'true' as const } : {}),
	}
}

/**
 * Label + description + error wrapper around any single form control.
 *
 * @remarks
 * - **Accessibility:** the label is bound to the control by `htmlFor`/`id`; the
 *   description and error are referenced from the control's `aria-describedby`;
 *   `aria-invalid="true"` is set exactly when a non-empty `error` is supplied.
 * - **Control-agnostic:** it clones its single child to inject the wiring, so it
 *   works over an `<input>`, `<textarea>`, or any control that forwards these
 *   attributes.
 * - **Prop-driven:** the description and error elements render only when their
 *   props are supplied.
 * - **Styling:** every colour, radius and spacing class is a `ds-*` token utility.
 *
 * @see packages/ui-components/src/field-shell/field-shell.stories.tsx for visual examples
 */
export function FieldShell({ label, description, error, children, className }: FieldShellProps): ReactElement {
	const baseId = useId()
	const hasError = Boolean(error)
	const aria = fieldAria(baseId, Boolean(description), hasError)

	return (
		<div className={cn('flex flex-col gap-1.5', className)}>
			<label id={`${baseId}-label`} htmlFor={aria.id} className="text-ds-sm font-medium text-ds-text-muted">
				{label}
			</label>
			{cloneElement(children, aria)}
			{description ? (
				<p data-testid="field-shell-description" id={`${baseId}-description`} className="text-ds-sm text-ds-text-muted">
					{description}
				</p>
			) : null}
			{hasError ? (
				<p data-testid="field-shell-error" id={`${baseId}-error`} className="text-ds-sm text-ds-danger">
					{error}
				</p>
			) : null}
		</div>
	)
}
